import React, { useEffect, useState } from 'react';
import {
  Alert, Avatar, Box, Button, Card, CardContent, Chip, Divider,
  FormControl, Grid, InputLabel, MenuItem, Select, Stack, TextField, Typography
} from '@mui/material';
import { AdminPanelSettingsOutlined, CampaignOutlined, Send as SendIcon } from '@mui/icons-material';
import { useMutation } from 'react-query';
import { notificationsAPI, usersAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

export default function Announcements() {
  const { user } = useAuth();
  const [buildings, setBuildings] = useState([]);
  const [buildingId, setBuildingId] = useState('');
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const userId = user?.userId || user?.attributes?.sub || user?.username;
    if (!userId) return;
    usersAPI.getAssociations(userId).then(response => {
      const unique = [...new Map((response.data || [])
        .filter(item => item.buildingId)
        .map(item => [item.buildingId, item])).values()];
      setBuildings(unique);
      setBuildingId(unique[0]?.buildingId || '');
    }).catch(() => setError('Unable to load your building information'));
  }, [user]);

  const send = useMutation(data => notificationsAPI.sendAnnouncementToAdmins(data), {
    onSuccess: response => {
      setSuccess(`Announcement sent to ${response.data.notificationsSent} building administrator(s).`);
      setError('');
      setTitle('');
      setMessage('');
    },
    onError: requestError => {
      setSuccess('');
      setError(requestError.response?.data?.message || 'Unable to send announcement');
    }
  });

  const handleSubmit = event => {
    event.preventDefault();
    setError('');
    setSuccess('');
    send.mutate({ buildingId, title: title.trim(), message: message.trim() });
  };

  const trimmedTitleLength = title.trim().length;
  const trimmedMessageLength = message.trim().length;
  const titleNeedsCharacters = Math.max(0, 5 - trimmedTitleLength);
  const messageNeedsCharacters = Math.max(0, 10 - trimmedMessageLength);
  const isValid = Boolean(buildingId) && titleNeedsCharacters === 0 && messageNeedsCharacters === 0;

  return (
    <Box sx={{
      minHeight: 'calc(100vh - 64px)',
      bgcolor: '#f6f8fb',
      pt: { xs: 2, sm: 4, md: 5 },
      pb: { xs: 'calc(104px + env(safe-area-inset-bottom, 0px))', md: 5 },
      px: { xs: 1.5, sm: 3 }
    }}>
      <Box sx={{ maxWidth: 860, mx: 'auto' }}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Avatar sx={{ width: 52, height: 52, bgcolor: 'primary.main', boxShadow: '0 8px 20px rgba(25,118,210,.2)' }}>
            <CampaignOutlined fontSize="large" />
          </Avatar>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: '-0.02em', fontSize: { xs: '1.75rem', sm: '2.2rem' } }}>
              Announcements
            </Typography>
            <Typography color="text.secondary">Contact your building management team</Typography>
          </Box>
        </Stack>

        <Alert
          icon={<AdminPanelSettingsOutlined />}
          severity="info"
          sx={{ mb: 3, border: '1px solid', borderColor: 'info.light', borderRadius: 2 }}
        >
        Your message will be sent only to the administrators of the selected building. They will receive an in-app notification and a push notification on their registered devices, enabling them to review and broadcast the message if required.        </Alert>

        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

        <Card sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', boxShadow: '0 12px 32px rgba(15,23,42,.07)', overflow: 'hidden' }}>
          <Box sx={{ px: { xs: 2.5, sm: 4 }, py: 2.5, bgcolor: 'rgba(25,118,210,.045)' }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>Compose message</Typography>
                <Typography variant="body2" color="text.secondary">Provide a clear subject and enough detail for the admin to respond.</Typography>
              </Box>
              <Chip label="Private to admins" size="small" color="primary" variant="outlined" />
            </Stack>
          </Box>
          <Divider />

          <CardContent sx={{ p: { xs: 2.5, sm: 4 }, '&:last-child': { pb: { xs: 2.5, sm: 4 } } }}>
            <Box component="form" onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <FormControl fullWidth required>
                    <InputLabel>Building</InputLabel>
                    <Select label="Building" value={buildingId} onChange={event => setBuildingId(event.target.value)}>
                      {buildings.map(building => (
                        <MenuItem key={building.buildingId} value={building.buildingId}>
                          {building.buildingName || building.buildingId}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth required label="Subject" placeholder="Example: Water leakage near Block A"
                    value={title} onChange={event => setTitle(event.target.value)}
                    inputProps={{ minLength: 5, maxLength: 120 }}
                    error={title.length > 0 && titleNeedsCharacters > 0}
                    helperText={titleNeedsCharacters > 0 ? `Enter ${titleNeedsCharacters} more character${titleNeedsCharacters === 1 ? '' : 's'} (${title.length}/120)` : `${title.length}/120 characters`}
                    FormHelperTextProps={{ sx: { textAlign: titleNeedsCharacters > 0 ? 'left' : 'right', mx: 0 } }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth required multiline minRows={6} label="Message"
                    placeholder="Describe the issue or information you want to share with the building admin..."
                    value={message} onChange={event => setMessage(event.target.value)}
                    inputProps={{ minLength: 10, maxLength: 2000 }}
                    error={message.length > 0 && messageNeedsCharacters > 0}
                    helperText={messageNeedsCharacters > 0 ? `Enter ${messageNeedsCharacters} more character${messageNeedsCharacters === 1 ? '' : 's'} (${message.length}/2000)` : `${message.length}/2000 characters`}
                    FormHelperTextProps={{ sx: { textAlign: messageNeedsCharacters > 0 ? 'left' : 'right', mx: 0 } }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Stack direction={{ xs: 'column-reverse', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} spacing={2}>
                    <Typography variant="caption" color="text.secondary">Required fields are marked with an asterisk.</Typography>
                    <Button
                      type="submit" size="large" variant="contained" startIcon={<SendIcon />}
                      disabled={!isValid || send.isLoading}
                      aria-describedby={!isValid ? 'announcement-validation-hint' : undefined}
                      sx={{ px: 3.5, py: 1.25, borderRadius: 2, fontWeight: 700, textTransform: 'none', boxShadow: isValid ? '0 8px 18px rgba(25,118,210,.25)' : 'none' }}
                    >
                      {send.isLoading ? 'Sending...' : 'Send to building admin'}
                    </Button>
                  </Stack>
                  {!isValid && <Typography id="announcement-validation-hint" variant="caption" color="text.secondary" display="block" sx={{ mt: 1, textAlign: { xs: 'center', sm: 'right' } }}>{!buildingId ? 'Select a building to continue.' : titleNeedsCharacters > 0 ? `Subject must contain at least 5 characters (${titleNeedsCharacters} more needed).` : `Message must contain at least 10 characters (${messageNeedsCharacters} more needed).`}</Typography>}
                </Grid>
              </Grid>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}
